import React, { useState } from 'react';
import { toast, ToastContainer } from 'react-toastify';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'react-toastify/dist/ReactToastify.css';
import 'leaflet/dist/leaflet.css';
import axios from 'axios';

// Fix for default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const states = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA',
  'MT', 'MS', 'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN',
  'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

const acquisitionTypes = [
  'Compra e Venda', 'Herança', 'Doação', 'Usucapião', 'Permuta',
  'Financiamento', 'Arrematação em Leilão', 'Dação em Pagamento',
  'Cessão de Direitos', 'Incorporação Imobiliária', 'Adjudicação',
  'Desapropriação', 'Outro'
];

const documentLabels = {
  matricula: 'Matrícula do Imóvel',
  escritura: 'Escritura Pública',
  iptu: 'IPTU',
  certidaoNegativa: 'Certidão Negativa de Débitos',
  certidaoOnus: 'Certidão de Ônus Reais',
  habiteseART: 'Habite-se / ART',
  laudoAvaliacao: 'Laudo de Avaliação',
  plantaBaixa: 'Planta Baixa',
  memoralDescritivo: 'Memorial Descritivo',
  licencaAmbiental: 'Licença Ambiental',
  outorgaAnatel: 'Outorga ANATEL',
  procuracaoRepresentante: 'Procuração do Representante'
};

const infrastructureLabels = {
  hasElectricity: 'Energia Elétrica',
  hasWater: 'Água',
  hasFiber: 'Fibra Óptica',
  hasAccess24h: 'Acesso 24 horas',
  hasSecurity: 'Vigilância/Segurança',
  hasParking: 'Estacionamento'
};

const initialFormData = {
  owner: {
    type: 'Pessoa Física',
    name: '',
    cpf: '',
    rg: '',
    cnpj: '',
    companyName: '',
    email: '',
    phone: '',
    whatsapp: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    }
  },
  property: {
    propertyName: '',
    acquisitionType: '',
    propertyType: '',
    availableArea: '',
    totalArea: '',
    expectedRent: '',
    documents: {
      matricula: false,
      escritura: false,
      iptu: false,
      certidaoNegativa: false,
      certidaoOnus: false,
      habiteseART: false,
      laudoAvaliacao: false,
      plantaBaixa: false,
      memoralDescritivo: false,
      licencaAmbiental: false,
      outorgaAnatel: false,
      procuracaoRepresentante: false
    },
    infrastructure: {
      hasElectricity: false,
      hasWater: false,
      hasFiber: false,
      hasAccess24h: false,
      hasSecurity: false,
      hasParking: false
    },
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    },
    location: {
      type: 'Point',
      coordinates: [-46.6333, -23.5505]
    },
    notes: ''
  }
};

function MapClickHandler({ onLocationSelect }) {
  useMapEvents({
    click: (e) => {
      onLocationSelect([e.latlng.lng, e.latlng.lat]);
    },
  });
  return null;
}

// Componente para centralizar o mapa quando as coordenadas mudarem
function MapCenterUpdater({ coordinates }) {
  const map = useMap();
  
  React.useEffect(() => {
    if (coordinates && coordinates.length === 2) {
      const [lng, lat] = coordinates;
      // Centraliza o mapa nas novas coordenadas com animação
      map.flyTo([lat, lng], 16, {
        duration: 1.5
      });
    }
  }, [coordinates, map]);
  
  return null;
}

function PublicRegistration() {
  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [loadingCep, setLoadingCep] = useState({ owner: false, property: false });

  // Função para buscar endereço pelo CEP (API ViaCEP)
  const searchCep = async (cep, section) => {
    // Remove caracteres não numéricos
    const cleanCep = cep.replace(/\D/g, '');
    
    if (cleanCep.length !== 8) {
      return;
    }

    setLoadingCep(prev => ({ ...prev, [section]: true }));

    try {
      const response = await axios.get(`https://viacep.com.br/ws/${cleanCep}/json/`);
      
      if (response.data.erro) {
        toast.error('CEP não encontrado');
        return;
      }

      const { logradouro, bairro, localidade, uf } = response.data;

      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          address: {
            ...prev[section].address,
            street: logradouro || prev[section].address.street,
            neighborhood: bairro || prev[section].address.neighborhood,
            city: localidade || prev[section].address.city,
            state: uf || prev[section].address.state,
            zipCode: cleanCep
          }
        }
      }));

      toast.success('✅ Endereço encontrado!');
    } catch (error) {
      toast.error('Erro ao buscar CEP. Verifique sua conexão.');
    } finally {
      setLoadingCep(prev => ({ ...prev, [section]: false }));
    }
  };

  // Validar CPF (algoritmo oficial)
  const validateCpf = (cpf) => {
    const cleaned = cpf.replace(/\D/g, '');
    
    if (cleaned.length !== 11) return false;
    
    // Verifica se todos os dígitos são iguais (ex: 111.111.111-11)
    if (/^(\d)\1+$/.test(cleaned)) return false;
    
    // Validação do primeiro dígito verificador
    let sum = 0;
    for (let i = 0; i < 9; i++) {
      sum += parseInt(cleaned.charAt(i)) * (10 - i);
    }
    let remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleaned.charAt(9))) return false;
    
    // Validação do segundo dígito verificador
    sum = 0;
    for (let i = 0; i < 10; i++) {
      sum += parseInt(cleaned.charAt(i)) * (11 - i);
    }
    remainder = (sum * 10) % 11;
    if (remainder === 10 || remainder === 11) remainder = 0;
    if (remainder !== parseInt(cleaned.charAt(10))) return false;
    
    return true;
  };

  // Formatar CPF enquanto digita (000.000.000-00)
  const formatCpf = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 3) return cleaned;
    if (cleaned.length <= 6) return `${cleaned.slice(0, 3)}.${cleaned.slice(3)}`;
    if (cleaned.length <= 9) return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6)}`;
    return `${cleaned.slice(0, 3)}.${cleaned.slice(3, 6)}.${cleaned.slice(6, 9)}-${cleaned.slice(9, 11)}`;
  };

  const handleCpfChange = (value) => {
    const formatted = formatCpf(value);
    handleChange('owner', 'cpf', formatted);
  };

  const handleCpfBlur = () => {
    const cpf = formData.owner.cpf;
    const cleaned = cpf.replace(/\D/g, '');
    
    if (cleaned.length === 11 && !validateCpf(cpf)) {
      toast.error('❌ CPF inválido! Verifique os números digitados.');
    } else if (cleaned.length === 11 && validateCpf(cpf)) {
      toast.success('✅ CPF válido!');
    }
  };

  // Validar CNPJ (algoritmo oficial)
  const validateCnpj = (cnpj) => {
    const cleaned = cnpj.replace(/\D/g, '');
    
    if (cleaned.length !== 14) return false;
    
    // Verifica se todos os dígitos são iguais
    if (/^(\d)\1+$/.test(cleaned)) return false;
    
    // Validação do primeiro dígito verificador
    let size = cleaned.length - 2;
    let numbers = cleaned.substring(0, size);
    const digits = cleaned.substring(size);
    let sum = 0;
    let pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    let result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(0))) return false;
    
    // Validação do segundo dígito verificador
    size = size + 1;
    numbers = cleaned.substring(0, size);
    sum = 0;
    pos = size - 7;
    
    for (let i = size; i >= 1; i--) {
      sum += parseInt(numbers.charAt(size - i)) * pos--;
      if (pos < 2) pos = 9;
    }
    
    result = sum % 11 < 2 ? 0 : 11 - (sum % 11);
    if (result !== parseInt(digits.charAt(1))) return false;
    
    return true;
  };

  // Formatar CNPJ enquanto digita (00.000.000/0000-00)
  const formatCnpj = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 2) return cleaned;
    if (cleaned.length <= 5) return `${cleaned.slice(0, 2)}.${cleaned.slice(2)}`;
    if (cleaned.length <= 8) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5)}`;
    if (cleaned.length <= 12) return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8)}`;
    return `${cleaned.slice(0, 2)}.${cleaned.slice(2, 5)}.${cleaned.slice(5, 8)}/${cleaned.slice(8, 12)}-${cleaned.slice(12, 14)}`;
  };

  const [loadingCnpj, setLoadingCnpj] = useState(false);

  const handleCnpjChange = (value) => {
    const formatted = formatCnpj(value);
    handleChange('owner', 'cnpj', formatted);
    
    // Auto-busca quando CNPJ completo (14 dígitos)
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length === 14 && validateCnpj(value)) {
      searchCnpj(cleaned);
    }
  };

  // Buscar dados do CNPJ na API da BrasilAPI (gratuita)
  const searchCnpj = async (cnpj) => {
    setLoadingCnpj(true);
    
    try {
      const response = await axios.get(`https://brasilapi.com.br/api/cnpj/v1/${cnpj}`);
      const data = response.data;
      
      // Preencher dados da empresa
      setFormData(prev => ({
        ...prev,
        owner: {
          ...prev.owner,
          companyName: data.razao_social || '',
          name: data.nome_fantasia || data.razao_social || '',
          email: data.email || prev.owner.email,
          phone: data.ddd_telefone_1 ? `(${data.ddd_telefone_1.substring(0,2)}) ${data.ddd_telefone_1.substring(2)}` : prev.owner.phone,
          address: {
            street: data.logradouro || '',
            number: data.numero || '',
            complement: data.complemento || '',
            neighborhood: data.bairro || '',
            city: data.municipio || '',
            state: data.uf || '',
            zipCode: data.cep ? formatCep(data.cep) : ''
          }
        }
      }));
      
      toast.success('✅ Dados da empresa encontrados e preenchidos!');
    } catch (error) {
      if (error.response?.status === 404) {
        toast.error('CNPJ não encontrado na base da Receita Federal');
      } else {
        toast.warning('Não foi possível buscar os dados. Preencha manualmente.');
      }
    } finally {
      setLoadingCnpj(false);
    }
  };

  const handleCnpjBlur = () => {
    const cnpj = formData.owner.cnpj;
    const cleaned = cnpj.replace(/\D/g, '');
    
    if (cleaned.length === 14 && !validateCnpj(cnpj)) {
      toast.error('❌ CNPJ inválido! Verifique os números digitados.');
    }
  };

  // Formatar Telefone enquanto digita
  const formatPhone = (value) => {
    const cleaned = value.replace(/\D/g, '');
    
    if (cleaned.length <= 2) return `(${cleaned}`;
    if (cleaned.length <= 7) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2)}`;
    if (cleaned.length <= 11) return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7)}`;
    return `(${cleaned.slice(0, 2)}) ${cleaned.slice(2, 7)}-${cleaned.slice(7, 11)}`;
  };

  const handlePhoneChange = (field, value) => {
    const formatted = formatPhone(value);
    handleChange('owner', field, formatted);
  };

  // Formatar CEP enquanto digita
  const formatCep = (value) => {
    const cleaned = value.replace(/\D/g, '');
    if (cleaned.length <= 5) {
      return cleaned;
    }
    return `${cleaned.slice(0, 5)}-${cleaned.slice(5, 8)}`;
  };

  const handleCepChange = (section, value) => {
    const formattedCep = formatCep(value);
    handleChange(section, 'address.zipCode', formattedCep);
    
    // Auto-busca quando CEP completo (8 dígitos)
    const cleanCep = value.replace(/\D/g, '');
    if (cleanCep.length === 8) {
      searchCep(cleanCep, section);
    }
  };

  // Formatar valor em Real (R$)
  const formatCurrency = (value) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleCurrencyChange = (section, field, value) => {
    const formatted = formatCurrency(value);
    handleChange(section, field, formatted);
  };

  // Converter valor formatado para número
  const parseCurrency = (value) => {
    if (!value) return 0;
    return parseFloat(value.replace(/\./g, '').replace(',', '.')) || 0;
  };

  // Formatar área em m² (decimal)
  const formatArea = (value) => {
    const numericValue = value.replace(/\D/g, '');
    if (!numericValue) return '';
    
    const number = parseInt(numericValue, 10) / 100;
    return number.toLocaleString('pt-BR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const handleAreaChange = (section, field, value) => {
    const formatted = formatArea(value);
    handleChange(section, field, formatted);
  };

  // Buscar coordenadas pelo endereço (Nominatim/OpenStreetMap)
  const geocodeAddress = async () => {
    const { address } = formData.property;
    
    if (!address.street || !address.city || !address.state) {
      return;
    }

    const query = `${address.street}, ${address.number || ''}, ${address.neighborhood}, ${address.city}, ${address.state}, Brasil`;
    
    try {
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=1`,
        {
          headers: {
            'User-Agent': 'TelecomERBsApp/1.0'
          }
        }
      );

      if (response.data && response.data.length > 0) {
        const { lat, lon } = response.data[0];
        setFormData(prev => ({
          ...prev,
          property: {
            ...prev.property,
            location: {
              type: 'Point',
              coordinates: [parseFloat(lon), parseFloat(lat)]
            }
          }
        }));
        toast.success('📍 Mapa centralizado no endereço do imóvel!');
      }
    } catch (error) {
      console.log('Erro na geocodificação:', error);
    }
  };

  const handleChange = (section, field, value) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [parent]: {
            ...prev[section][parent],
            [child]: value
          }
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [section]: {
          ...prev[section],
          [field]: value
        }
      }));
    }
  };

  const handleCheckboxChange = (section, parent, field, checked) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [parent]: {
          ...prev[section][parent],
          [field]: checked
        }
      }
    }));
  };

  const handleLocationSelect = (coordinates) => {
    setFormData(prev => ({
      ...prev,
      property: {
        ...prev.property,
        location: {
          type: 'Point',
          coordinates: coordinates
        }
      }
    }));
    toast.info('📍 Localização marcada no mapa!');
  };

  const validateStep = (step) => {
    if (step === 1) {
      const { owner } = formData;
      if (!owner.name || !owner.email || !owner.phone) {
        toast.error('Preencha todos os campos obrigatórios do proprietário');
        return false;
      }
      if (owner.type === 'Pessoa Física') {
        if (!owner.cpf) {
          toast.error('CPF é obrigatório para Pessoa Física');
          return false;
        }
        if (!validateCpf(owner.cpf)) {
          toast.error('❌ CPF inválido! Verifique os números digitados.');
          return false;
        }
      }
      if (owner.type === 'Pessoa Jurídica') {
        if (!owner.cnpj) {
          toast.error('CNPJ é obrigatório para Pessoa Jurídica');
          return false;
        }
        if (!validateCnpj(owner.cnpj)) {
          toast.error('❌ CNPJ inválido! Verifique os números digitados.');
          return false;
        }
      }
    }
    if (step === 2) {
      const { property } = formData;
      if (!property.propertyType || !property.acquisitionType || !property.availableArea) {
        toast.error('Preencha todos os campos obrigatórios do imóvel');
        return false;
      }
      if (!property.address.street || !property.address.neighborhood || 
          !property.address.city || !property.address.state) {
        toast.error('Preencha o endereço completo do imóvel');
        return false;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      // Se está indo para etapa 3, busca coordenadas do endereço
      if (currentStep === 2) {
        geocodeAddress();
      }
      setCurrentStep(prev => prev + 1);
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => prev - 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateStep(1) || !validateStep(2)) return;

    setLoading(true);

    try {
      // Converter valores formatados para números antes de enviar
      const dataToSend = {
        ...formData,
        property: {
          ...formData.property,
          totalArea: parseCurrency(formData.property.totalArea),
          expectedRent: parseCurrency(formData.property.expectedRent)
        }
      };

      await axios.post(`${API_URL}/public/register`, dataToSend);
      setSubmitted(true);
      toast.success('Cadastro realizado com sucesso!');
    } catch (error) {
      const message = error.response?.data?.message || 'Erro ao realizar cadastro';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const mapCenter = formData.property.location?.coordinates 
    ? [formData.property.location.coordinates[1], formData.property.location.coordinates[0]]
    : [-23.5505, -46.6333];

  if (submitted) {
    return (
      <div className="public-page">
        <div className="public-container">
          <div className="success-card">
            <div className="success-icon">✅</div>
            <h1>Cadastro Realizado!</h1>
            <p>Seu imóvel foi cadastrado com sucesso em nossa base de dados.</p>
            <p>Nossa equipe entrará em contato em breve para dar continuidade ao processo, assim que houver interesse de alguma Empresa de Infraestrutura de Telecomunicação.</p>
            <div className="success-buttons">
              <button 
                className="btn btn-primary btn-lg"
                onClick={() => {
                  setSubmitted(false);
                  setFormData(initialFormData);
                  setCurrentStep(1);
                }}
              >
                Cadastrar Outro Imóvel
              </button>
              <button 
                className="btn btn-secondary btn-lg"
                onClick={() => {
                  window.close();
                  // Fallback caso window.close() não funcione (restrições do navegador)
                  window.location.href = 'about:blank';
                }}
              >
                ✕ Fechar Página
              </button>
            </div>
          </div>
        </div>
        <ToastContainer position="top-right" autoClose={4000} />
      </div>
    );
  }

  return (
    <div className="public-page">
      <div className="public-header">
        <div className="public-header-content">
          <div className="public-logo">📡</div>
          <h1>Cadastro de Imóvel para ERB</h1>
          <p>Cadastre seu imóvel para locação de espaço para Estação Rádio Base de Telecomunicações</p>
        </div>
      </div>

      <div className="public-container">
        {/* Progress Steps */}
        <div className="steps-indicator">
          <div className={`step ${currentStep >= 1 ? 'active' : ''} ${currentStep > 1 ? 'completed' : ''}`}>
            <div className="step-number">1</div>
            <div className="step-label">Proprietário</div>
          </div>
          <div className="step-line"></div>
          <div className={`step ${currentStep >= 2 ? 'active' : ''} ${currentStep > 2 ? 'completed' : ''}`}>
            <div className="step-number">2</div>
            <div className="step-label">Imóvel</div>
          </div>
          <div className="step-line"></div>
          <div className={`step ${currentStep >= 3 ? 'active' : ''}`}>
            <div className="step-number">3</div>
            <div className="step-label">Localização</div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Proprietário */}
          {currentStep === 1 && (
            <div className="form-card">
              <div className="form-card-header">
                <h2>👤 Dados do Proprietário</h2>
                <p>Informe seus dados pessoais ou da empresa</p>
              </div>
              <div className="form-card-body">
                <div className="form-group">
                  <label className="form-label required">Tipo de Pessoa</label>
                  <div className="radio-group">
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="ownerType"
                        checked={formData.owner.type === 'Pessoa Física'}
                        onChange={() => handleChange('owner', 'type', 'Pessoa Física')}
                      />
                      <span>Pessoa Física</span>
                    </label>
                    <label className="radio-option">
                      <input
                        type="radio"
                        name="ownerType"
                        checked={formData.owner.type === 'Pessoa Jurídica'}
                        onChange={() => handleChange('owner', 'type', 'Pessoa Jurídica')}
                      />
                      <span>Pessoa Jurídica</span>
                    </label>
                  </div>
                </div>

                {formData.owner.type === 'Pessoa Física' ? (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">CPF</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="000.000.000-00"
                          maxLength={14}
                          value={formData.owner.cpf}
                          onChange={(e) => handleCpfChange(e.target.value)}
                          onBlur={handleCpfBlur}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label">RG</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Número do RG"
                          value={formData.owner.rg}
                          onChange={(e) => handleChange('owner', 'rg', e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">Nome Completo</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Seu nome completo"
                          value={formData.owner.name}
                          onChange={(e) => handleChange('owner', 'name', e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">CNPJ</label>
                        <div className="input-with-loading">
                          <input
                            type="text"
                            className="form-input"
                            placeholder="00.000.000/0000-00"
                            maxLength={18}
                            value={formData.owner.cnpj}
                            onChange={(e) => handleCnpjChange(e.target.value)}
                            onBlur={handleCnpjBlur}
                          />
                          {loadingCnpj && <span className="input-loading">🔍</span>}
                        </div>
                        <small className="field-hint">Digite o CNPJ para preencher os dados automaticamente</small>
                      </div>
                    </div>
                    <div className="form-row">
                      <div className="form-group">
                        <label className="form-label required">Razão Social</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Nome da empresa"
                          value={formData.owner.companyName}
                          onChange={(e) => handleChange('owner', 'companyName', e.target.value)}
                        />
                      </div>
                      <div className="form-group">
                        <label className="form-label required">Nome do Responsável</label>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Nome do responsável"
                          value={formData.owner.name}
                          onChange={(e) => handleChange('owner', 'name', e.target.value)}
                        />
                      </div>
                    </div>
                  </>
                )}

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">E-mail</label>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="seu@email.com"
                      value={formData.owner.email}
                      onChange={(e) => handleChange('owner', 'email', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Telefone</label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      value={formData.owner.phone}
                      onChange={(e) => handlePhoneChange('phone', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">WhatsApp</label>
                    <input
                      type="tel"
                      className="form-input"
                      placeholder="(00) 00000-0000"
                      maxLength={15}
                      value={formData.owner.whatsapp}
                      onChange={(e) => handlePhoneChange('whatsapp', e.target.value)}
                    />
                  </div>
                </div>
              </div>
              <div className="form-card-footer">
                <div></div>
                <button type="button" className="btn btn-primary" onClick={nextStep}>
                  Próximo →
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Imóvel */}
          {currentStep === 2 && (
            <div className="form-card">
              <div className="form-card-header">
                <h2>🏢 Dados do Imóvel</h2>
                <p>Informe os dados do imóvel disponível para locação</p>
              </div>
              <div className="form-card-body">
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Nome/Identificação do Imóvel</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Ex: Terreno Centro, Cobertura Ed. Central"
                      value={formData.property.propertyName}
                      onChange={(e) => handleChange('property', 'propertyName', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Tipo de Imóvel</label>
                    <select
                      className="form-select"
                      value={formData.property.propertyType}
                      onChange={(e) => handleChange('property', 'propertyType', e.target.value)}
                    >
                      <option value="">Selecione</option>
                      <option value="Terreno">Terreno</option>
                      <option value="Cobertura">Cobertura</option>
                      <option value="Laje">Laje</option>
                      <option value="Telhado">Telhado</option>
                      <option value="Torre Existente">Torre Existente</option>
                      <option value="Outro">Outro</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Tipo de Aquisição</label>
                    <select
                      className="form-select"
                      value={formData.property.acquisitionType}
                      onChange={(e) => handleChange('property', 'acquisitionType', e.target.value)}
                    >
                      <option value="">Selecione</option>
                      {acquisitionTypes.map(type => (
                        <option key={type} value={type}>{type}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Área Disponível para Locação</label>
                    <select
                      className="form-select"
                      value={formData.property.availableArea}
                      onChange={(e) => handleChange('property', 'availableArea', e.target.value)}
                    >
                      <option value="">Selecione</option>
                      <option value="Terreno integral">Terreno Integral</option>
                      <option value="10x10">10x10 (100m²)</option>
                      <option value="10x15">10x15 (150m²)</option>
                      <option value="15x15">15x15 (225m²)</option>
                      <option value="15x20">15x20 (300m²)</option>
                      <option value="20x20">20x20 (400m²)</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Área Total do Terreno</label>
                    <div className="input-with-suffix">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="0,00"
                        value={formData.property.totalArea}
                        onChange={(e) => handleAreaChange('property', 'totalArea', e.target.value)}
                      />
                      <span className="input-suffix">m²</span>
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Valor Pretendido (mensal)</label>
                    <div className="input-with-prefix">
                      <span className="input-prefix">R$</span>
                      <input
                        type="text"
                        className="form-input"
                        placeholder="0,00"
                        value={formData.property.expectedRent}
                        onChange={(e) => handleCurrencyChange('property', 'expectedRent', e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                <div className="section-divider"></div>

                <h3 className="section-title">📍 Endereço do Imóvel</h3>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">CEP</label>
                    <div className="cep-input-wrapper">
                      <input
                        type="text"
                        className="form-input"
                        placeholder="00000-000"
                        maxLength={9}
                        value={formData.property.address.zipCode}
                        onChange={(e) => handleCepChange('property', e.target.value)}
                      />
                      {loadingCep.property && <span className="cep-loading">🔍</span>}
                    </div>
                    <small className="cep-hint">Digite o CEP para preencher o endereço automaticamente</small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ flex: 2 }}>
                    <label className="form-label required">Logradouro</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Rua, Avenida, etc."
                      value={formData.property.address.street}
                      onChange={(e) => handleChange('property', 'address.street', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Número</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Nº"
                      value={formData.property.address.number}
                      onChange={(e) => handleChange('property', 'address.number', e.target.value)}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label required">Bairro</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Bairro"
                      value={formData.property.address.neighborhood}
                      onChange={(e) => handleChange('property', 'address.neighborhood', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Cidade</label>
                    <input
                      type="text"
                      className="form-input"
                      placeholder="Cidade"
                      value={formData.property.address.city}
                      onChange={(e) => handleChange('property', 'address.city', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label required">Estado</label>
                    <select
                      className="form-select"
                      value={formData.property.address.state}
                      onChange={(e) => handleChange('property', 'address.state', e.target.value)}
                    >
                      <option value="">UF</option>
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="section-divider"></div>

                <h3 className="section-title">📄 Documentos Disponíveis</h3>
                <p className="section-description">Marque os documentos que você possui:</p>
                <div className="checkbox-grid">
                  {Object.entries(documentLabels).map(([key, label]) => (
                    <label key={key} className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.property.documents[key]}
                        onChange={(e) => handleCheckboxChange('property', 'documents', key, e.target.checked)}
                      />
                      <span className="form-checkbox-label">{label}</span>
                    </label>
                  ))}
                </div>

                <div className="section-divider"></div>

                <h3 className="section-title">⚡ Infraestrutura Existente</h3>
                <div className="checkbox-grid">
                  {Object.entries(infrastructureLabels).map(([key, label]) => (
                    <label key={key} className="form-checkbox">
                      <input
                        type="checkbox"
                        checked={formData.property.infrastructure[key]}
                        onChange={(e) => handleCheckboxChange('property', 'infrastructure', key, e.target.checked)}
                      />
                      <span className="form-checkbox-label">{label}</span>
                    </label>
                  ))}
                </div>

                <div className="section-divider"></div>

                <h3 className="section-title">📝 Observações</h3>
                <div className="form-group">
                  <textarea
                    className="form-textarea"
                    placeholder="Informações adicionais sobre o imóvel..."
                    value={formData.property.notes}
                    onChange={(e) => handleChange('property', 'notes', e.target.value)}
                    rows={3}
                  />
                </div>
              </div>
              <div className="form-card-footer">
                <button type="button" className="btn btn-secondary" onClick={prevStep}>
                  ← Voltar
                </button>
                <button type="button" className="btn btn-primary" onClick={nextStep}>
                  Próximo →
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Localização no Mapa */}
          {currentStep === 3 && (
            <div className="form-card">
              <div className="form-card-header">
                <h2>🗺️ Localização no Mapa</h2>
                <p>Clique no mapa para marcar o ponto exato do imóvel</p>
              </div>
              <div className="form-card-body">
                <div className="map-wrapper">
                  <MapContainer 
                    center={mapCenter} 
                    zoom={13} 
                    style={{ height: '450px', width: '100%', borderRadius: '12px' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapClickHandler onLocationSelect={handleLocationSelect} />
                    <MapCenterUpdater coordinates={formData.property.location?.coordinates} />
                    {formData.property.location?.coordinates && (
                      <Marker position={[formData.property.location.coordinates[1], formData.property.location.coordinates[0]]} />
                    )}
                  </MapContainer>
                </div>
                <div className="map-info">
                  <div className="coordinates-display">
                    📍 <strong>Coordenadas:</strong> {formData.property.location?.coordinates?.[1]?.toFixed(6)}, {formData.property.location?.coordinates?.[0]?.toFixed(6)}
                  </div>
                  <p className="map-help">💡 Dica: Use o scroll do mouse para dar zoom e clique para marcar a localização</p>
                </div>
              </div>
              <div className="form-card-footer">
                <button type="button" className="btn btn-secondary" onClick={prevStep}>
                  ← Voltar
                </button>
                <button type="submit" className="btn btn-success btn-lg" disabled={loading}>
                  {loading ? 'Enviando...' : '✓ Finalizar Cadastro'}
                </button>
              </div>
            </div>
          )}
        </form>
      </div>

      <footer className="public-footer">
        <p>© 2024 Sistema de Cadastro de Imóveis para ERBs - Telecomunicações</p>
      </footer>

      <ToastContainer position="top-right" autoClose={4000} />

      <style>{`
        .public-page {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
        }

        .public-header {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          padding: 60px 20px;
          text-align: center;
          color: white;
          position: relative;
          overflow: hidden;
        }

        .public-header::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }

        .public-header-content {
          position: relative;
          z-index: 1;
          max-width: 700px;
          margin: 0 auto;
        }

        .public-logo {
          font-size: 64px;
          margin-bottom: 20px;
          animation: pulse 2s infinite;
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }

        .public-header h1 {
          font-size: 32px;
          font-weight: 700;
          margin-bottom: 12px;
          text-shadow: 0 2px 4px rgba(0,0,0,0.2);
        }

        .public-header p {
          font-size: 16px;
          opacity: 0.9;
        }

        .public-container {
          max-width: 900px;
          margin: -40px auto 40px;
          padding: 0 20px;
          position: relative;
          z-index: 2;
        }

        .steps-indicator {
          display: flex;
          align-items: center;
          justify-content: center;
          background: white;
          padding: 24px;
          border-radius: 16px;
          margin-bottom: 24px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
        }

        .step {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 8px;
        }

        .step-number {
          width: 40px;
          height: 40px;
          border-radius: 50%;
          background: #e2e8f0;
          color: #718096;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 16px;
          transition: all 0.3s ease;
        }

        .step.active .step-number {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          transform: scale(1.1);
          box-shadow: 0 4px 15px rgba(102, 126, 234, 0.4);
        }

        .step.completed .step-number {
          background: #48bb78;
          color: white;
        }

        .step-label {
          font-size: 13px;
          color: #718096;
          font-weight: 500;
        }

        .step.active .step-label {
          color: #667eea;
          font-weight: 600;
        }

        .step-line {
          width: 80px;
          height: 3px;
          background: #e2e8f0;
          margin: 0 16px;
          margin-bottom: 24px;
          border-radius: 2px;
        }

        .form-card {
          background: white;
          border-radius: 16px;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
          overflow: hidden;
        }

        .form-card-header {
          background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
          padding: 28px 32px;
          border-bottom: 1px solid #e2e8f0;
        }

        .form-card-header h2 {
          font-size: 22px;
          font-weight: 700;
          color: #1a202c;
          margin-bottom: 6px;
        }

        .form-card-header p {
          font-size: 14px;
          color: #718096;
        }

        .form-card-body {
          padding: 32px;
        }

        .form-card-footer {
          padding: 20px 32px;
          background: #f7fafc;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .radio-group {
          display: flex;
          gap: 24px;
        }

        .radio-option {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
          padding: 12px 20px;
          border: 2px solid #e2e8f0;
          border-radius: 10px;
          transition: all 0.2s ease;
        }

        .radio-option:hover {
          border-color: #667eea;
        }

        .radio-option input:checked + span {
          color: #667eea;
          font-weight: 600;
        }

        .radio-option:has(input:checked) {
          border-color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .section-divider {
          height: 1px;
          background: #e2e8f0;
          margin: 28px 0;
        }

        .section-title {
          font-size: 16px;
          font-weight: 600;
          color: #1a202c;
          margin-bottom: 12px;
        }

        .section-description {
          font-size: 13px;
          color: #718096;
          margin-bottom: 16px;
        }

        .map-wrapper {
          border-radius: 12px;
          overflow: hidden;
          box-shadow: 0 4px 20px rgba(0,0,0,0.1);
        }

        .map-info {
          margin-top: 16px;
          padding: 16px;
          background: #f7fafc;
          border-radius: 10px;
        }

        .coordinates-display {
          font-size: 14px;
          color: #4a5568;
          margin-bottom: 8px;
        }

        .map-help {
          font-size: 13px;
          color: #718096;
          margin: 0;
        }

        .success-card {
          background: white;
          border-radius: 16px;
          padding: 60px 40px;
          text-align: center;
          box-shadow: 0 10px 40px rgba(0,0,0,0.2);
          margin-top: 60px;
        }

        .success-icon {
          font-size: 72px;
          margin-bottom: 24px;
        }

        .success-card h1 {
          font-size: 28px;
          color: #1a202c;
          margin-bottom: 16px;
        }

        .success-card p {
          font-size: 16px;
          color: #718096;
          margin-bottom: 12px;
        }

        .success-buttons {
          display: flex;
          gap: 16px;
          justify-content: center;
          margin-top: 32px;
          flex-wrap: wrap;
        }

        .success-buttons .btn {
          min-width: 200px;
        }

        .public-footer {
          text-align: center;
          padding: 24px;
          color: rgba(255,255,255,0.6);
          font-size: 13px;
        }

        .btn-lg {
          padding: 14px 32px;
          font-size: 16px;
        }

        .btn-success {
          background: linear-gradient(135deg, #48bb78 0%, #38a169 100%);
          color: white;
        }

        .btn-success:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(72, 187, 120, 0.4);
        }

        .cep-input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .cep-input-wrapper .form-input {
          padding-right: 40px;
        }

        .cep-loading {
          position: absolute;
          right: 12px;
          animation: spin 1s linear infinite;
        }

        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }

        .cep-hint {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #718096;
        }

        .input-with-prefix,
        .input-with-suffix {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-prefix {
          position: absolute;
          left: 14px;
          color: #667eea;
          font-weight: 600;
          font-size: 14px;
          z-index: 1;
        }

        .input-suffix {
          position: absolute;
          right: 14px;
          color: #667eea;
          font-weight: 600;
          font-size: 14px;
        }

        .input-with-prefix .form-input {
          padding-left: 45px;
        }

        .input-with-suffix .form-input {
          padding-right: 45px;
        }

        .input-with-loading {
          position: relative;
          display: flex;
          align-items: center;
        }

        .input-with-loading .form-input {
          padding-right: 40px;
        }

        .input-loading {
          position: absolute;
          right: 12px;
          animation: spin 1s linear infinite;
        }

        .field-hint {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          color: #667eea;
          font-style: italic;
        }

        @media (max-width: 768px) {
          .public-header {
            padding: 40px 20px;
          }

          .public-header h1 {
            font-size: 24px;
          }

          .public-logo {
            font-size: 48px;
          }

          .steps-indicator {
            padding: 16px;
            flex-wrap: wrap;
            gap: 12px;
          }

          .step-line {
            width: 40px;
            margin: 0 8px;
          }

          .form-card-body {
            padding: 20px;
          }

          .radio-group {
            flex-direction: column;
          }
        }
      `}</style>
    </div>
  );
}

export default PublicRegistration;
